import React, { useCallback, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { formatCurrency } from '@atc/string-fns';

import {
    Button,
    Col,
    ErrorMessages,
    Glyphicon,
    LinkIcon,
    LoadingSpinner,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle,
    Row,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { EmailCTA } from 'reaxl-email';
import {
    Form,
    StandardZipInput,
} from 'reaxl-forms';
import { InfoIcon } from 'reaxl-molecules';

import { ownerChangeLocationClick } from '@/analytics/srp/owner/ownerActionEvents';
import { ownerGetQuickEstimateClick } from '@/analytics/srp/owner/ownerInquiryEvents';

function LongHaulDeliveryCard({
    getMarket = () => {},
    getQuickEstimates = () => {},
    onEmailCTAClick = () => {},
    deliveryQuoteClick = false,
    extendedHomeDelivery = {},
    isLoading = false,
    listingId = '',
    userZip = '',
    uiContext = 'primary',
}) {
    const [showZipModal, setShowZipModal] = useState(false);
    const [zipCode, setZipCode] = useState(userZip);
    const [showInValidZip, setShowInValidZip] = useState(false);
    const userEnteredZip = useRef(userZip);
    const { estimatedShippingCost, estimatedDates, results } = extendedHomeDelivery;

    const handleToggleModal = useCallback(() => {
        setShowZipModal(!showZipModal);
        setShowInValidZip(false);
    }, [setShowZipModal, showZipModal]);

    const onZipSubmit = useCallback(async (event) => {
        const zip = userEnteredZip.current;
        if (zip?.toString().length === 5) {
            const response = await getMarket(zip); // checking for valid ZIP code
            if (response?.success) {
                setZipCode(zip);
                getQuickEstimates(zip);
                sendClick(ownerChangeLocationClick, event, { zip });
                handleToggleModal();
            } else {
                setShowInValidZip(true);
            }
        }
    }, [userEnteredZip, setZipCode, handleToggleModal, setShowInValidZip, getMarket, getQuickEstimates]);

    const handleChangeZip = (e) => {
        userEnteredZip.current = e.target.value;
        setShowInValidZip(false);
    };

    const handleQuickEstimateClick = (event) => {
        sendClick(ownerGetQuickEstimateClick, event, {});
        getQuickEstimates();
    };

    const renderZipCodeModal = () => (
        <Modal
            show={showZipModal}
            onHide={handleToggleModal}
            bsSize="small"
        >
            <ModalHeader closeButton>
                <ModalTitle>Change Your Location</ModalTitle>
            </ModalHeader>
            <ModalBody className="display-flex justify-content-center">
                <Form>
                    <StandardZipInput
                        label="ZIP Code"
                        name="zip"
                        value={zipCode}
                        onChange={handleChangeZip}
                        required
                    />
                    {showInValidZip && (
                        <ErrorMessages errors="Enter a valid ZIP Code" />
                    )}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button
                    onClick={handleToggleModal}
                    bsStyle="link"
                >
                    Cancel
                </Button>
                <Button
                    onClick={onZipSubmit}
                    bsStyle="primary"
                >
                    Change
                </Button>
            </ModalFooter>
        </Modal>
    );

    const renderDefault = (
        <div>
            <Text
                id="extendedDeliveryDescription"
                componentClass="span"
            >
                Find out more about how to get this car delivered.
            </Text>
            {' '}
            <Button
                bsStyle="text"
                onClick={handleQuickEstimateClick}
            >
                Get a quick estimate.
            </Button>
        </div>
    );

    const renderInfoCard = () => {
        const renderZip = (
            <>
                <Text
                    weight="bold"
                    componentClass="div"
                >
                    ZIP Code
                </Text>
                <LinkIcon
                    glyph="edit"
                    onClick={handleToggleModal}
                    undecorated
                >
                    {zipCode}
                </LinkIcon>
            </>
        );

        const renderLongFormCTA = (
            <Row>
                <Col
                    componentClass={EmailCTA}
                    xs={8}
                    sm={7}
                    md={8}
                    onClick={onEmailCTAClick}
                    type="link"
                    labelKey="longHaulDeliveryLongForm"
                    listingId={listingId}
                />
                <Col
                    xs={4}
                    sm={5}
                    md={4}
                >
                    {!!zipCode && renderZip}
                </Col>
            </Row>
        );

        const shortFormCTA = (
            <EmailCTA
                onClick={onEmailCTAClick}
                type="link"
                labelKey="longHaulDeliveryShortForm"
                listingId={listingId}
            />
        );

        const renderDetails = (
            <>
                <div>
                    <Text
                        weight="bold"
                        componentClass="div"
                    >
                        Est. Arrival
                    </Text>
                    { estimatedDates ? `${estimatedDates}` : shortFormCTA }
                </div>

                <div>
                    <Text
                        weight="bold"
                        componentClass="div"
                    >
                        Est. Shipping
                    </Text>
                    { estimatedShippingCost ? `${formatCurrency(Math.round(estimatedShippingCost))}` : shortFormCTA }
                </div>
                <div>
                    {!!zipCode && renderZip}
                </div>
            </>
        );

        return isLoading ? <LoadingSpinner className="margin-top-3" /> : (
            <div className="margin-top-1 display-flex justify-content-between">
                { Object.keys(extendedHomeDelivery).length >= 1 && results !== false ? renderDetails : renderLongFormCTA }
            </div>
        );
    };

    return (
        <div
            className="display-flex"
            data-cmp="long-haul-delivery-card"
        >
            <Glyphicon
                className={clsx({ 'text-accent align-self-center': uiContext === 'primary' }, 'margin-right-3 text-size-500')}
                glyph="delivery-truck"
                aria-hidden="true"
            />
            <div className="display-flex flex-column flex-grow">
                <div>
                    <Text
                        className="margin-right-1"
                        weight="bold"
                    >
                        Delivery
                    </Text>
                    <InfoIcon
                        popoverId="longHaulDeliveryInfo"
                        infoText="Some dealers from outside your local area offer the option to deliver a car to you. Distance and shipping fees may apply."
                    />
                </div>
                {deliveryQuoteClick ? renderInfoCard() : renderDefault}
            </div>
            {renderZipCodeModal()}
        </div>
    );
}

LongHaulDeliveryCard.propTypes = {
    /**
     * Function to fetch ZIP code validation data
     */
    getMarket: PropTypes.func,
    /**
     * Callback for initial Get Estimate click
     */
    getQuickEstimates: PropTypes.func,
    /**
     * Callback for Contact Dealer click
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * Boolean to display data (true) or inital quote request (false)
     */
    deliveryQuoteClick: PropTypes.bool,
    /**
     * Results object from Delivery API
     */
    extendedHomeDelivery: PropTypes.object,
    /**
     * Displays loading status if `deliveryQuoteClick` is also true, but there's no data available
     */
    isLoading: PropTypes.bool,
    /**
     * Listing ID
     */
    listingId: PropTypes.string,
    /**
     * ZIP code for use in initial quote request
     */
    userZip: PropTypes.string,
    uiContext: PropTypes.oneOf(['primary', 'default']),
};

export default LongHaulDeliveryCard;
